import { Button, Checkbox, Dropdown, Field, Input, Label, Option, makeStyles, Spinner, Text, Title3 } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { dolphinTokens } from "../../../utils/Theme";
import { useState } from "react";
import Status from "../../../enums/Status";
import recruitmentService from "../../../services/RecruitmentService";
import VacancyDropdown from "./VacancyDropdown";
import GenderDropdown from "./GenderDropdown";
import SkillCheckbox from "./SkillCheckbox";
import EmploymentStatusDropdown from "./EmploymentStatusDropdown";
import FileField from "./FileField";
import IApplication, { NewApplication } from "../../../models/recruitment/IApplication";

const useStyles = makeStyles({
    section: {
        margin: "0 auto",
        padding: "32px 16px",
        marginLeft: "auto",
        marginRight: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: 1,
        minWidth: "300px",
        maxWidth: "700px"
    },
    card: {
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        padding: "24px",
        marginBottom: "0",
        maxWidth: "700",
        width: "100%",
        textAlign: "left"
    },
    cardTitle: {
        marginBottom: "8",
        color: dolphinTokens.colorLightBlue,
    },
    form: {
        maxWidth: "600px",
        margin: "0 auto",

        "& input[type='file']": {
            display: "none"
        }
    },
    formRow: {
        display: "flex",
        gap: "16px",
        marginBottom: "20px"
    },
    formCol: {
        flex: 1
    },
    otherVacancy: {
        marginBottom: "20px"
    },
    skills: {
        flex: 1,
        marginTop: "28px"
    },
    otherSkill: {
        marginBottom: "20px"
    },
    chkAgree: {
        marginBottom: "20px"
    },
    applyContainer: {
        textAlign: "center",
        marginBottom: "16px"
    },
    message: {
        textAlign: "center",
        color: dolphinTokens.colorLightBlue
    }
});

export default function ApplicationForm() {
    const { t } = useTranslation("recruitment");
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.NEURAL);
    const [form, setForm] = useState<IApplication>(NewApplication);
    const [resume, setResume] = useState<File>(null!);
    const [coverLetter, setCoverLetter] = useState<File>(null!);

    const handleChange = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));

    const handleFileChange = (field: string, files: FileList | null) => {
        if (field === "resume" && files && files.length > 0) {
            setResume(files[0]);
        } else if (field === "resume") {
            setResume(null!);
        } else if (field === "coverLetter" && files && files.length > 0) {
            setCoverLetter(files[0]);
        } else if (field === "coverLetter") {
            setCoverLetter(null!);
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!resume || !coverLetter) {
            alert(t("register.form.fileRequired"));
            return;
        }
        
        await submitApplication(form, resume, coverLetter, setStatus);
    };

    const isDisabled = [Status.LOADING, Status.SAVING, Status.LOAD_ERROR, Status.SAVE_SUCCESS].includes(status);
    
    return (
        <section className={styles.section}>
            <Title3 as="h2" block className={styles.cardTitle}>
                {t("register.title")}
            </Title3>

            <div className={styles.card}>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formRow}>
                        <Field label={t("register.form.firstName")} className={styles.formCol} required>
                            <Input
                                value={form.firstName}
                                disabled={isDisabled}
                                onChange={e => handleChange("firstName", e.target.value)}
                            />
                        </Field>
                        <Field label={t("register.form.lastName")} className={styles.formCol} required>
                            <Input
                                value={form.lastName}
                                disabled={isDisabled}
                                onChange={e => handleChange("lastName", e.target.value)}
                                required
                            />
                        </Field>
                    </div>
                    <div className={styles.formRow}>
                        <Field label={t("register.form.email")} className={styles.formCol} required>
                            <Input
                                type="email"
                                disabled={isDisabled}
                                value={form.emailAddress}
                                onChange={e => handleChange("emailAddress", e.target.value)}
                                required
                            />
                        </Field>
                        <Field label={t("register.form.number")} className={styles.formCol} required>
                            <Input
                                type="tel"
                                disabled={isDisabled}
                                value={form.phoneNumber}
                                onChange={e => handleChange("phoneNumber", e.target.value)}
                            />
                        </Field>
                    </div>
                    <div className={styles.formRow}>
                        <Field label={t("register.form.location")} className={styles.formCol} required>
                            <Input
                                value={form.location}
                                disabled={isDisabled}
                                onChange={e => handleChange("location", e.target.value)}
                            />
                        </Field>

                        <GenderDropdown
                            selectedGenderId={form.gender}
                            isDisabled={isDisabled}
                            onGenderChange={(genderId: string) => handleChange("gender", genderId)}
                            />
                    </div>
                    <div className={styles.formRow}>
                        <FileField
                            labelKey="register.form.resume"
                            isDisabled={isDisabled}
                            file={resume!}
                            onFileChange={files => handleFileChange("resume", files!)}
                            />
                        <FileField
                            labelKey="register.form.coverLetter"
                            isDisabled={isDisabled}
                            file={coverLetter!}
                            onFileChange={files => handleFileChange("coverLetter", files!)}
                            />
                    </div>

                    <VacancyDropdown
                        selectedVacancyId={form.vacancy}
                        disabled={isDisabled}
                        onVacancyChange={vacancyId => handleChange("vacancy", vacancyId)}
                        />

                    <div className={styles.otherVacancy}>
                        <Field label={t("register.form.otherVacancy")}>
                            <Input
                                value={form.otherVacancy}
                                onChange={e => handleChange("otherVacancy", e.target.value)}
                            />
                        </Field>
                    </div>

                    <div className={styles.formRow}>
                        <EmploymentStatusDropdown
                            selectedEmploymentStatusId={form.status}
                            isDisabled={isDisabled}
                            onEmploymentStatusChange={(employmentStatusId: string) => handleChange("status", employmentStatusId)}
                            />

                        <Field label={t("register.form.expectation")} className={styles.formCol}>
                            <Input
                                type="number"
                                min={0}
                                step="any"
                                value={form.expectation !== null && form.expectation !== undefined ? String(form.expectation) : ""}
                                onChange={e => {
                                    const value = e.target.value;
                                    handleChange("expectation", value === "" ? null : parseFloat(value));
                                }}
                            />
                        </Field>
                    </div>
                    <div className={styles.formRow}>
                        <div className={styles.formCol}>
                            <Label weight="semibold">
                                {t("register.form.skillsLabel")}
                            </Label>
                            <SkillCheckbox skill="skill_dotnet" isChecked={form.skill_dotnet} onChange={handleChange} />
                            <SkillCheckbox skill="skill_sql" isChecked={form.skill_sql} onChange={handleChange} />
                            <SkillCheckbox skill="skill_m365_consultancy" isChecked={form.skill_m365_consultancy} onChange={handleChange} />
                            <SkillCheckbox skill="skill_m365_management" isChecked={form.skill_m365_management} onChange={handleChange} />
                            <SkillCheckbox skill="skill_spfx" isChecked={form.skill_spfx} onChange={handleChange} />
                            <SkillCheckbox skill="skill_unit_testing" isChecked={form.skill_unit_testing} onChange={handleChange} />
                        </div>
                        <div className={styles.skills}>
                            <SkillCheckbox skill="skill_pbi" isChecked={form.skill_pbi} onChange={handleChange} />
                            <SkillCheckbox skill="skill_papps" isChecked={form.skill_papps} onChange={handleChange} />
                            <SkillCheckbox skill="skill_msflow" isChecked={form.skill_msflow} onChange={handleChange} />
                            <SkillCheckbox skill="skill_documentation" isChecked={form.skill_documentation} onChange={handleChange} />
                            <SkillCheckbox skill="skill_pm" isChecked={form.skill_pm} onChange={handleChange} />
                            <SkillCheckbox skill="skill_sales_marketing" isChecked={form.skill_sales_marketing} onChange={handleChange} />
                            <SkillCheckbox skill="skill_itsupport" isChecked={form.skill_itsupport} onChange={handleChange} />
                        </div>
                    </div>
                    <div className={styles.otherSkill}>
                        <Field label={t("register.form.skill_other")}>
                            <Input
                                value={form.skill_other}
                                placeholder={t("register.form.otherSkillPlaceholder")}
                                onChange={e => setForm(f => ({ ...f, skill_other: e.target.value }))}
                                />
                        </Field>
                    </div>
                    <div className={styles.chkAgree}>
                        <Checkbox
                            checked={form.agree}
                            onChange={(_, d) => handleChange("agree", !!d.checked)}
                            required
                            label={<span dangerouslySetInnerHTML={{ __html: t("register.form.agree") }} />}
                        />
                    </div>
                    <div className={styles.applyContainer}>
                        <Button appearance="primary" type="submit" disabled={isDisabled || !form.agree}>
                            {   
                                status === Status.SAVING
                                    ? <Spinner size="tiny" />
                                    : t("register.form.applyNow")
                            }
                        </Button>
                    </div>

                    {
                        status === Status.SAVE_ERROR &&
                        <div className={styles.message}>
                            {t("register.form.error")}
                        </div>
                    }
                    {
                        status === Status.SAVE_SUCCESS &&
                        <div className={styles.message}>
                            {t("register.form.thankYou")}
                        </div>
                    }
                </form>

            </div>
        </section>
    );
}

async function submitApplication (
    form: IApplication,
    resume: File,
    coverLetter: File,
    setStatus: (status: Status) => void
) {
    setStatus(Status.SAVING);

    try {
        await recruitmentService.submitApplication(form, resume, coverLetter);
        setStatus(Status.SAVE_SUCCESS);
    } catch (error) {
        setStatus(Status.SAVE_ERROR);
    }
}